
import React, { memo, useEffect } from "react";
import { Movie } from "@/types/movie";
import { useMovieCard } from "@/hooks/useMovieCard";
import UndoCard from "./UndoCard";
import MovieCardGrid from "./cards/MovieCardGrid";
import HorizontalMovieCard from "./cards/HorizontalMovieCard";
import { logRender, isDebugMovie } from "@/utils/debugLogger";
import { LIST_NAMES } from "@/hooks/search/types/listConstants";
import { useMovieContext } from "@/context";

interface MovieCardProps {
  movie: Movie;
  currentTab?: string;
  viewMode?: "grid" | "list";
  onOpenMovieDetails: (tmdbId: number) => void;
  isSearchResult?: boolean;
  cardKey?: string; // NEW: Optional dynamic key for React reconciliation
}

// Using memo to prevent unnecessary re-renders
const MovieCard = memo<MovieCardProps>(({ 
  movie, 
  currentTab,
  viewMode = "grid",
  onOpenMovieDetails,
  isSearchResult = false,
  cardKey // NEW: Accept external key
}) => {
  // FIXED: All hooks must be called before any conditional logic
  const shouldDebug = isDebugMovie(movie);
  const { getPendingAction, undoPendingAction } = useMovieContext();
  
  const {
    isHovered,
    showAsFavorite,
    showAsPlanToWatch,
    handleOpenDialog,
    handleToggleFavorite,
    handleTogglePlanToWatch,
    setIsHovered
  } = useMovieCard(movie, currentTab, onOpenMovieDetails, isSearchResult);

  // Use effect to log when important props change
  useEffect(() => {
    if (shouldDebug) {
      logRender("MovieCard props changed:", {
        title: movie.title,
        showAsFavorite,
        showAsPlanToWatch,
        cardKey
      });
    }
  }, [movie.tmdbId, movie.title, showAsFavorite, showAsPlanToWatch, shouldDebug, cardKey]);

  // FIXED: Now check for missing tmdbId AFTER all hooks have been called
  if (!movie.tmdbId) {
    console.warn('MovieCard: Movie without tmdbId:', movie);
    return null;
  }

  if (shouldDebug) {
    logRender("MovieCard rendering:", {
      title: movie.title,
      tmdbId: movie.tmdbId, 
      viewMode, 
      isSearchResult,
      cardKey
    });
  }
  
  // Check for pending action based on current tab - using TMDB ID consistently
  let pendingAction = null;
  if (!isSearchResult && currentTab) {
    if (currentTab === LIST_NAMES.FAVORITES || currentTab === LIST_NAMES.UNSEEN_GEMS) {
      pendingAction = getPendingAction(movie.tmdbId, 'favorite');
    } else if (currentTab === LIST_NAMES.WATCHLIST) {
      pendingAction = getPendingAction(movie.tmdbId, 'planToWatch');
    }
  }

  // If there's a pending action, show the UndoCard
  if (pendingAction && !isSearchResult) {
    return (
      <div key={`undo-${pendingAction.id}`} data-movie-tmdb-id={movie.tmdbId}>
        <UndoCard 
          movie={movie} 
          action={pendingAction}
          onUndo={undoPendingAction}
          viewMode={viewMode}
        />
      </div>
    );
  }

  // Consistent dialog handler that uses TMDB ID
  const handleOpenDialogWithId = () => {
    console.log("MovieCard: Opening movie with TMDB ID:", movie.tmdbId);
    onOpenMovieDetails(movie.tmdbId);
  };

  // FIXED: Use stable key that excludes favorite/planToWatch status to prevent remounts
  const dynamicCardKey = cardKey || `tmdb-${movie.tmdbId}-${viewMode}-${currentTab}-${isSearchResult ? 'search' : 'regular'}`;

  // Render list view
  if (viewMode === "list") {
    return (
      <div key={dynamicCardKey} data-movie-tmdb-id={movie.tmdbId}>
        <HorizontalMovieCard
          movie={movie}
          variant={isSearchResult ? "search" : "list"}
          currentTab={currentTab}
          isHovered={isHovered}
          showAsFavorite={showAsFavorite}
          showAsPlanToWatch={showAsPlanToWatch}
          handleOpenDialog={handleOpenDialogWithId}
          handleToggleFavorite={handleToggleFavorite}
          handleTogglePlanToWatch={handleTogglePlanToWatch}
          setIsHovered={setIsHovered}
        />
      </div>
    );
  }

  // Default grid view
  return (
    <div key={dynamicCardKey} data-movie-tmdb-id={movie.tmdbId}>
      <MovieCardGrid
        movie={movie}
        currentTab={currentTab}
        isHovered={isHovered}
        handleOpenDialog={handleOpenDialogWithId}
        handleToggleFavorite={handleToggleFavorite}
        handleTogglePlanToWatch={handleTogglePlanToWatch}
        showAsFavorite={showAsFavorite}
        showAsPlanToWatch={showAsPlanToWatch}
        setIsHovered={setIsHovered}
      />
    </div>
  );
});

MovieCard.displayName = "MovieCard";

export default MovieCard;
